import React from 'react';
import { Clock, Zap, Calendar } from 'lucide-react';

/**
 * Prominent delivery-time label used on job cards, bid dialogs, map info
 * windows and customer delivery cards.
 *
 * Accepts the raw `preferred_time` string stored on deliveries — either a
 * JSON blob `{type: 'before'|'range'|'flexible', date, before?, from?, to?}`
 * or a legacy ISO datetime string. Renders a coloured pill that reads as a
 * single action (“Deliver before 2:00 PM”).
 *
 * Props:
 *   preferredTime:  raw string
 *   size:           'sm' | 'md' | 'lg'  (default 'md')
 *   className
 *   dataTestId
 */
export const DeliveryTimeBadge = ({
  preferredTime,
  size = 'md',
  className = '',
  dataTestId,
}) => {
  const info = parse(preferredTime);
  if (!info) return null;

  const sizeCls =
    size === 'lg'
      ? 'text-sm sm:text-base px-3 py-1.5 gap-2'
      : size === 'sm'
      ? 'text-[11px] px-2 py-0.5 gap-1'
      : 'text-xs sm:text-sm px-2.5 py-1 gap-1.5';

  const iconSize =
    size === 'lg' ? 'w-4 h-4 sm:w-5 sm:h-5' : size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5 sm:w-4 sm:h-4';

  const { tone, Icon, primary, secondary } = info;
  const toneCls = TONE_CLASSES[tone] || TONE_CLASSES.default;

  return (
    <span
      className={`inline-flex items-center rounded-full font-bold ${sizeCls} ${toneCls} ${className}`}
      data-testid={dataTestId}
      data-tone={tone}
    >
      <Icon className={iconSize} strokeWidth={2.5} />
      <span className="leading-tight">
        {primary}
        {secondary && (
          <span className="ml-1.5 font-medium opacity-80">· {secondary}</span>
        )}
      </span>
    </span>
  );
};

// ── helpers ──────────────────────────────────────────────────────────────

const TONE_CLASSES = {
  urgent: 'bg-red-100 text-red-800 ring-1 ring-red-200',
  today: 'bg-amber-100 text-amber-900 ring-1 ring-amber-200',
  soon: 'bg-blue-50 text-blue-900 ring-1 ring-blue-200',
  flexible: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200',
  default: 'bg-gray-100 text-gray-800 ring-1 ring-gray-200',
};

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function fmt12h(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return t;
  const minutes = (m || '00').padStart(2, '0');
  const hour12 = ((hr + 11) % 12) + 1;
  const suffix = hr >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes} ${suffix}`;
}

function fmtShortDate(d, includeYear = false) {
  const now = new Date();
  const day = startOfDay(d).getTime();
  const today = startOfDay(now).getTime();
  if (day === today) return 'Today';
  if (day === today + 86400000) return 'Tomorrow';
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  if (includeYear || d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-IE', opts);
}

function parse(raw) {
  if (!raw) return null;

  // Try JSON shape first.
  try {
    const p = JSON.parse(raw);
    if (p && p.type && p.date) return parseStructured(p);
  } catch (_) {
    // fall through to legacy parsing below
  }

  // Legacy ISO datetime (pre-structured feature) — treat as "before <time>".
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return buildBeforeInfo(d, d);
}

function parseStructured(p) {
  const date = new Date(`${p.date}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  if (p.type === 'flexible') {
    const dateLabel = fmtShortDate(date);
    return {
      tone: dateLabel === 'Today' ? 'today' : 'flexible',
      Icon: Calendar,
      primary: 'Flexible delivery',
      secondary: dateLabel,
    };
  }

  if (p.type === 'range' && p.from && p.to) {
    const dateLabel = fmtShortDate(date);
    const window = `${fmt12h(p.from)} – ${fmt12h(p.to)}`;
    return {
      tone: dateLabel === 'Today' ? 'today' : 'soon',
      Icon: Clock,
      primary: `Deliver ${window}`,
      secondary: dateLabel,
    };
  }

  // default: before
  const [h, m] = (p.before || '00:00').split(':').map((x) => parseInt(x, 10));
  const when = new Date(date);
  if (!Number.isNaN(h)) when.setHours(h, m || 0, 0, 0);
  return buildBeforeInfo(when, date);
}

function buildBeforeInfo(whenTs, dateOnly) {
  const now = new Date();
  const diffMs = whenTs.getTime() - now.getTime();
  const hoursAway = diffMs / 3_600_000;
  const dateLabel = fmtShortDate(dateOnly);

  // Past or < 2h in the future = ASAP tone.
  if (hoursAway <= 2) {
    return {
      tone: 'urgent',
      Icon: Zap,
      primary: 'ASAP delivery',
      secondary: `by ${fmt12h(formatClock(whenTs))}`,
    };
  }
  // Same-day: amber "Today" treatment.
  if (dateLabel === 'Today') {
    return {
      tone: 'today',
      Icon: Clock,
      primary: `Deliver before ${fmt12h(formatClock(whenTs))}`,
      secondary: 'Today',
    };
  }
  return {
    tone: 'soon',
    Icon: Clock,
    primary: `Deliver before ${fmt12h(formatClock(whenTs))}`,
    secondary: dateLabel,
  };
}

function formatClock(d) {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export default DeliveryTimeBadge;
